import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { SubscriptionButton } from '@/components/subscription-button';
import { Badge } from '@/components/ui/badge';
import { Check, Sparkles, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PricingCardProps {
  title: string;
  description: string;
  price: number;
  priceId: string;
  features: string[];
  popular?: boolean;
  mode?: 'payment' | 'subscription';
  credits?: number;
  billingPeriod?: string;
  className?: string;
  compact?: boolean;
}

export function PricingCard({
  title,
  description,
  price,
  priceId,
  features,
  popular,
  mode = 'subscription',
  credits,
  billingPeriod = '주',
  className,
  compact = false,
}: PricingCardProps) {
  return (
    <Card className={cn(
      `relative overflow-hidden transition-all duration-300 hover:translate-y-[-4px] ${popular ? 'border-primary shadow-xl shadow-primary/10 dark:shadow-primary/5' : 'hover:shadow-md'}`,
      className
    )}>
      {/* Corner decoration */}
      {popular && !compact && (
        <div className="absolute right-0 top-0 overflow-hidden rounded-bl-lg">
          <div className="flex h-[28px] w-[28px] items-center justify-center rounded-sm bg-primary px-3 py-1 text-xs font-medium text-primary-foreground shadow-sm">
            <Sparkles className="h-3.5 w-3.5" />
          </div>
        </div>
      )}
      
      {/* Popular badge with increased top margin and improved visibility */}
      {popular && (
        <div className="absolute top-0 left-0 w-full flex justify-center">
          <span className="mt-1 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-md z-10">
            인기 상품
          </span>
        </div>
      )}
      
      <CardHeader className={cn(
        `pb-6 ${popular ? 'pt-10' : 'pt-6'}`,
        compact && 'p-4 pb-3 pt-8'
      )}>
        <CardTitle className={cn("text-xl", compact && "text-lg")}>{title}</CardTitle>
        <CardDescription className={cn(
          "mt-1.5 line-clamp-2 h-10",
          compact && "text-xs h-8"
        )}>{description}</CardDescription>
      </CardHeader>
      
      <CardContent className={cn("space-y-5 pb-6", compact && "space-y-3 p-4 pt-0 pb-4")}>
        <div className="flex items-baseline">
          <span className={cn("text-3xl font-bold", compact && "text-2xl")}>
            {new Intl.NumberFormat('ko-KR').format(price)}
          </span>
          <span className={cn("ml-1.5 text-muted-foreground", compact && "text-xs ml-1")}>
            원{mode === 'subscription' ? `/${billingPeriod}` : ''}
          </span>
        </div>
        
        {credits && (
          <div className={cn(
            "flex items-center gap-2 rounded-md bg-primary/10 p-2.5 text-sm",
            compact && "p-2 text-xs"
          )}>
            <div className="rounded-full bg-primary/20 p-1.5">
              <Zap className={cn("h-4 w-4 text-primary", compact && "h-3 w-3")} />
            </div>
            <span className="font-medium text-primary">
              {credits.toLocaleString()} 크레딧 {mode === 'subscription' ? `매${billingPeriod}` : '즉시'} 제공
            </span>
          </div>
        )}
        
        <ul className={cn("space-y-3", compact && "space-y-2")}>
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2">
              <div className={cn(
                "mt-0.5 rounded-full bg-primary/10 p-1",
                compact && "mt-0 p-0.5"
              )}>
                <Check className={cn("h-3 w-3 text-primary", compact && "h-2.5 w-2.5")} />
              </div>
              <span className={cn("text-sm", compact && "text-xs")}>{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      
      <CardFooter className={cn("pb-12", compact && "p-4 pt-0 pb-8")}> {/* Increased bottom padding for badge */}
        <SubscriptionButton 
          priceId={priceId} 
          mode={mode}
          className={cn(
            popular ? 'gradient-button' : '',
            compact && 'text-sm py-1.5'
          )}
        >
          {mode === 'subscription' ? '가입하고 시작하기' : '구매하기'}
        </SubscriptionButton>
      </CardFooter>
      
      {/* Bottom badge with better positioning and styling */}
      {popular && (
        <div className="absolute bottom-3 left-0 w-full flex justify-center">
          <Badge 
            className="bg-accent/10 text-accent px-2 py-1 text-xs whitespace-nowrap"
          >
            가장 많이 선택한 상품
          </Badge>
        </div>
      )}
    </Card>
  );
}